import React, {useEffect, useState} from 'react';
import { Box, Typography } from "@mui/material";
import { makeStyles } from "@material-ui/core/styles";
import ChatBot from "../components/ChatBot.comp";
import {useHistory, useLocation} from "react-router-dom";
import {CHAT_TYPE} from "../../../utils/constants";
import {
    createProgramService,
    fetchProgramService,
    CurationPromptService,
    CurationPromptStreamService, 
} from "../../../services/learning_programs.service";
import Loader from "../../../components/Loader/Loader.comp";

const useStyles = makeStyles((theme) => ({
    pageContainer: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '88vh',
        padding: '32px',
        position: 'relative',
        [theme.breakpoints.down('sm')]: {
            padding: '16px',
        },
    },
    contentContainer: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        marginBottom: (props) => props.chatInitiated ? '0' : '120px',
        transition: 'all 0.3s ease',
    },
    title: {
        background: 'linear-gradient(to right, #6A8EC9 10%, #8C74A7 35%, #D44C71 59%, #C91C6E 100%)',
        color: 'transparent',
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        fontWeight: '500 !important',
        fontSize: '45px !important',
        marginBottom: '24px !important',
        [theme.breakpoints.down('sm')]: {
            fontSize: "28px !important",
        },
    },
    subtitle: {
        color: '#ED6628',
        fontSize: '26px',
        marginBottom: '16px',
        [theme.breakpoints.down('sm')]: {
            fontSize: '17px',
        },
    },
    examplesContainer: {
        padding: '24px',
        margin: '24px 0',
        maxWidth: '800px',
        display: (props) => props.chatInitiated ? 'none' : 'block',
        transition: 'all 0.3s ease',
    },
}));

const DQProProgCreation = () => {
    const [chatInitiated, setChatInitiated] = useState(false);
    const classes = useStyles({ chatInitiated });

    const [chatHistory, setChatHistory] = useState([]);
    const [input, setInput] = useState('');
    const [showChat, setShowChat] = useState(false);
    const [showLoader, setShowLoader] = useState(false);
    const [isBotThinking, setIsBotThinking] = useState(false);
    const [programIdState, setProgramIdState] = useState(null);

    const history = useHistory();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const programId = queryParams.get('programId');
    const [detectedCourse, setDetectedCourse] = useState({});

    // Input handlers
    const handleInputChange = (e) => setInput(e.target.value);

    const sendMessage = async (user_message) => {
        if (!user_message.trim()) return;

        setShowChat(true);
        setInput('');
        setChatInitiated(true);

        let currentProgramId = programIdState;

        // 1. If there is no program yet, create it first, THEN send message
        if (!currentProgramId) {
            setShowLoader(true);
            try {
                const result = await createProgramService();
                if (result?.data?.program_id) {
                    currentProgramId = result.data.program_id;
                    setProgramIdState(currentProgramId);
                    setShowLoader(false);
                    // Now send the user message (and don't return yet!)
                    await actuallySendMessage(currentProgramId, user_message);
                    return; // Stop here so code below doesn't run
                }
            } catch (e) {
                setShowLoader(false);
                return;
            }
        } else {
            // If we already have a program, send the user message as usual
            await actuallySendMessage(currentProgramId, user_message);
        }
    };

    // Helper that actually posts user message & streams bot reply
    const actuallySendMessage = async (programId, user_message) => {
        setChatHistory(prev => [
            ...prev,
            { type: CHAT_TYPE.USER, text: user_message }
        ]);

        setIsBotThinking(true);
        try {
            const response = await CurationPromptStreamService(programId, user_message);
            if (!response.ok) throw new Error('Network response was not ok');
            if (!response.body) throw new Error('Readable stream not supported in your browser.');
            const reader = response.body.getReader();
            const decoder = new TextDecoder('utf-8');
            let botMessage = '';
            let doneStreaming = false;
            while (!doneStreaming) {
                const { done, value } = await reader.read();
                if (done) break;
                const chunk = decoder.decode(value);
                const lines = chunk.split('data: ');
                for (let line of lines) {
                    const content = line;
                    if (content === '[DONE]') {
                        setIsBotThinking(false);
                        doneStreaming = true;
                        break;
                    } else {
                        botMessage += content;
                        if (botMessage.includes("{{redirect_to_outline=true}}")) {
                            botMessage = botMessage.replace("{{redirect_to_outline=true}}", "");

                            // Attempt to extract detected_course from botMessage
                            let detectedCourseObj = undefined;
                            const marker = "{{detected_course=";
                            const start = botMessage.indexOf(marker);
                            if (start !== -1) {
                                const valueStart = start + marker.length;
                                const valueEnd = botMessage.indexOf("}}", valueStart);
                                if (valueEnd !== -1) {
                                    const encoded = botMessage.substring(valueStart, valueEnd);
                                    try {
                                        detectedCourseObj = JSON.parse(decodeURIComponent(encoded));
                                    } catch (e) {
                                        // Fallback: don't break if JSON parse fails
                                        detectedCourseObj = undefined;
                                    }
                                    // Remove the detected_course marker from message
                                    botMessage = botMessage.replace(`${marker}${encoded}}}`, "");
                                }
                            }
                            console.log('detectedCourse:', detectedCourseObj);
                            history.push({
                                pathname: '/dq_pro/products/program/name',
                                state: { detectedCourse: detectedCourseObj }
                            });
                        }

                        setChatHistory(prev => {
                            // append or update last bot message
                            if (prev.length > 0 && prev[prev.length - 1].type === CHAT_TYPE.BOT) {
                                const updated = [...prev];
                                updated[updated.length - 1].text = botMessage;
                                return updated;
                            } else {
                                return [...prev, { type: CHAT_TYPE.BOT, text: botMessage }];
                            }
                        });
                    }
                }
            }
            // Check redirect header
            const redirectFlag = response.headers.get('redirect_to_outline');
            if (redirectFlag && programId) {
                history.push({
                    pathname: '/dq_pro/products/program/name',
                    state: { detectedCourse }
                });
            }
            setIsBotThinking(false);
        } catch (e) {
            setIsBotThinking(false);
        }
    };


    const handleInputKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage(input).then(r => console.log(r));
        }
    };

    const handleSendClick = () => {
        sendMessage(input).then(r => console.log(r));
    };

    if (showLoader) return <Loader />;

    return (
        <Box className={classes.pageContainer}>
            <Box className={classes.contentContainer}>
                <Typography variant="h2" className={classes.title} sx={{ mb: 2 }}>
                    Welcome to DQ Learning Creator!
                </Typography>

                <Box className={classes.examplesContainer}>
                    <Typography variant="h5" sx={{ textAlign: 'center', mb: 4 }}>
                        Let's begin designing your custom AI Literacy learning Program.<br/>To start please tell us about the main topic or theme you want to teach.
                    </Typography>
                    <Typography variant="h5" style={{ color:'#EE6D29' }}>
                        Examples:
                    </Typography>
                    <Typography variant="h5" style={{ marginBottom: '15px', fontStyle:'italic', color:'#EE6D29' }}>
                        "I want to create a program to teach about cyberbullying for Grade 5 students." <br/>
                        "I want to explore AI Ethics with middle school learners."
                    </Typography>
                </Box>
            </Box>

            <Box>
                <ChatBot
                    chatHistory={chatHistory}
                    input={input}
                    onInputChange={handleInputChange}
                    onInputKeyDown={handleInputKeyDown}
                    onSendClick={handleSendClick}
                    showChat={showChat}
                    isBotThinking={isBotThinking}
                />
            </Box>
        </Box>
    );
};

export default DQProProgCreation;